import seoData from './seoData.json';

const siteSeo = seoData.site;
const routeMap = Object.fromEntries(seoData.routes.map((route) => [route.path, route]));
const navigationRoutes = seoData.routes.filter((route) => route.includeInNavigation !== false);

export { siteSeo };

export const getRouteSeo = (path) => routeMap[path];

export const getAbsoluteUrl = (path = '/') => {
  const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
  return new URL(normalizedPath, siteSeo.url).toString();
};

export const getKeywords = (route) =>
  [...new Set([...(route?.keywords || []), ...(siteSeo.keywords || [])])].join(', ');

const buildNavigationId = (path) =>
  `${siteSeo.url}#nav-${path === '/' ? 'home' : path.replace(/^\//, '').replace(/\//g, '-')}`;

export const primaryNavigation = navigationRoutes.map((route) => ({
  path: route.path,
  label: route.navLabel || route.name,
  schemaName: route.schemaName || route.name,
  summary: route.summary,
  url: getAbsoluteUrl(route.path)
}));

/* ── Individual schema builders ── */

const buildOrganizationSchema = () => {
  const orgId = `${siteSeo.url}#organization`;
  const logoId = `${siteSeo.url}#logo`;

  return {
    '@type': 'Organization',
    '@id': orgId,
    name: siteSeo.name,
    alternateName: siteSeo.alternateName,
    legalName: siteSeo.legalName || siteSeo.name,
    url: siteSeo.url,
    description: siteSeo.description,
    foundingDate: siteSeo.foundingDate,
    email: siteSeo.email,
    areaServed: siteSeo.areaServed,
    numberOfEmployees: siteSeo.numberOfEmployees
      ? {
          '@type': 'QuantitativeValue',
          minValue: siteSeo.numberOfEmployees.minValue,
          maxValue: siteSeo.numberOfEmployees.maxValue
        }
      : undefined,
    knowsAbout: siteSeo.knowsAbout || [],
    logo: {
      '@type': 'ImageObject',
      '@id': logoId,
      url: siteSeo.image,
      width: siteSeo.imageWidth,
      height: siteSeo.imageHeight,
      caption: siteSeo.name
    },
    image: {
      '@id': logoId
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: siteSeo.contactEmail || siteSeo.email,
      availableLanguage: {
        '@type': 'Language',
        name: 'English'
      },
      areaServed: siteSeo.areaServed
    },
    sameAs: siteSeo.sameAs
  };
};

const buildWebSiteSchema = () => ({
  '@type': 'WebSite',
  '@id': `${siteSeo.url}#website`,
  url: siteSeo.url,
  name: siteSeo.name,
  alternateName: siteSeo.alternateName,
  description: siteSeo.description,
  inLanguage: 'en',
  publisher: {
    '@id': `${siteSeo.url}#organization`
  }
});

const buildLogoImageSchema = () => ({
  '@type': 'ImageObject',
  '@id': `${siteSeo.url}#logo`,
  url: siteSeo.image,
  width: siteSeo.imageWidth,
  height: siteSeo.imageHeight,
  caption: siteSeo.imageAlt
});

const buildPrimaryImageSchema = (pageUrl) => ({
  '@type': 'ImageObject',
  '@id': `${pageUrl}#primaryimage`,
  url: siteSeo.image,
  width: siteSeo.imageWidth,
  height: siteSeo.imageHeight,
  caption: siteSeo.imageAlt
});

const buildWebPageSchema = (path, route, pageUrl) => ({
  '@type': route.pageType || 'WebPage',
  '@id': `${pageUrl}#webpage`,
  url: pageUrl,
  name: route.title,
  description: route.description,
  datePublished: route.datePublished,
  dateModified: route.dateModified,
  inLanguage: 'en',
  isPartOf: {
    '@id': `${siteSeo.url}#website`
  },
  about: {
    '@id': `${siteSeo.url}#organization`
  },
  primaryImageOfPage: {
    '@id': `${pageUrl}#primaryimage`
  },
  breadcrumb: path !== '/' ? { '@id': `${pageUrl}#breadcrumb` } : undefined
});

const buildNavElementSchema = (link) => ({
  '@type': 'SiteNavigationElement',
  '@id': buildNavigationId(link.path),
  name: link.schemaName,
  description: link.summary,
  url: link.url,
  isPartOf: {
    '@id': `${siteSeo.url}#website`
  },
  inLanguage: 'en'
});

const buildItemListSchema = (route, pageUrl) => {
  if (!route.structuredItems || route.structuredItems.length === 0) return null;

  const isRockets = route.path === '/rockets';

  return {
    '@type': 'ItemList',
    '@id': `${pageUrl}#itemlist`,
    name: isRockets
      ? 'Antaral Aerospace Launch Vehicle Portfolio'
      : 'Antaral Aerospace Mission Portfolio',
    description: route.description,
    numberOfItems: route.structuredItems.length,
    itemListOrder: 'https://schema.org/ItemListOrderAscending',
    itemListElement: route.structuredItems.map((item) => ({
      '@type': 'ListItem',
      position: item.position,
      item: isRockets
        ? {
            '@type': 'Product',
            name: item.name,
            description: item.description,
            identifier: item.identifier,
            category: item.category,
            brand: {
              '@type': 'Brand',
              name: siteSeo.name
            },
            manufacturer: {
              '@id': `${siteSeo.url}#organization`
            }
          }
        : {
            '@type': 'Thing',
            name: item.name,
            description: item.description,
            identifier: item.identifier
          }
    }))
  };
};

const buildServiceSchemas = (pageUrl) =>
  (siteSeo.services || []).map((service, index) => ({
    '@type': 'Service',
    '@id': `${pageUrl}#service-${index + 1}`,
    name: service.name,
    description: service.description,
    serviceType: service.serviceType,
    provider: {
      '@id': `${siteSeo.url}#organization`
    },
    areaServed: siteSeo.areaServed,
    availableChannel: {
      '@type': 'ServiceChannel',
      serviceUrl: siteSeo.url
    }
  }));

const buildContactPageSchema = (route, pageUrl) => {
  if (!route.contactPoint) return null;

  return {
    '@type': 'ContactPage',
    '@id': `${pageUrl}#contactpage`,
    url: pageUrl,
    name: route.title,
    description: route.description,
    isPartOf: {
      '@id': `${siteSeo.url}#website`
    },
    about: {
      '@id': `${siteSeo.url}#organization`
    },
    inLanguage: 'en'
  };
};

const buildFAQSchema = (route, pageUrl) => {
  if (!route.faq || route.faq.length === 0) return null;

  return {
    '@type': 'FAQPage',
    '@id': `${pageUrl}#faqpage`,
    mainEntity: route.faq.map((item, index) => ({
      '@type': 'Question',
      '@id': `${pageUrl}#faq-${index + 1}`,
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer
      }
    }))
  };
};

/* ── Breadcrumb ── */
export const buildBreadcrumbSchema = (path) => {
  const route = getRouteSeo(path);
  if (!route || path === '/') return null;

  const pageUrl = getAbsoluteUrl(path);

  return {
    '@type': 'BreadcrumbList',
    '@id': `${pageUrl}#breadcrumb`,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: getAbsoluteUrl('/')
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: route.name,
        item: pageUrl
      }
    ]
  };
};

/* ── Main structured data builder ── */
export const buildStructuredData = (path) => {
  const route = getRouteSeo(path);
  if (!route) return null;

  const pageUrl = getAbsoluteUrl(path);
  const graph = [];

  // Core entities — always included on every page
  graph.push(buildOrganizationSchema());
  graph.push(buildWebSiteSchema());
  graph.push(buildLogoImageSchema());
  graph.push(buildPrimaryImageSchema(pageUrl));
  graph.push(buildWebPageSchema(path, route, pageUrl));

  // Navigation elements
  primaryNavigation.forEach((link) => graph.push(buildNavElementSchema(link)));

  // ── Page-specific schemas ──

  // Home page: rich service listing
  if (path === '/') {
    buildServiceSchemas(pageUrl).forEach((s) => graph.push(s));
  }

  // Missions & Rockets: ItemList of all items
  if (route.structuredItems) {
    const itemList = buildItemListSchema(route, pageUrl);
    if (itemList) graph.push(itemList);
  }

  // Contact page: ContactPage + ContactPoint schema
  if (path === '/contact') {
    const contactPage = buildContactPageSchema(route, pageUrl);
    if (contactPage) graph.push(contactPage);
  }

  // Start mission: service schema for rideshare offering
  if (path === '/start-mission') {
    buildServiceSchemas(pageUrl)
      .filter((s) => s.name === 'Rideshare Launch Booking')
      .forEach((s) => graph.push(s));
  }

  // FAQ — whenever the route has FAQ entries
  const faq = buildFAQSchema(route, pageUrl);
  if (faq) graph.push(faq);

  // Breadcrumb for all non-home pages
  const breadcrumb = buildBreadcrumbSchema(path);
  if (breadcrumb) graph.push(breadcrumb);

  return {
    '@context': 'https://schema.org',
    '@graph': graph.filter(Boolean)
  };
};
